import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, Calendar, FileText, MessageSquare, 
  LogOut, Menu, X, ChevronRight, Upload, Download, Trash2, Send, CreditCard
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ScrollArea } from '../components/ui/scroll-area';

const API = `${window.location.origin}/api`;

const sidebarLinks = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/portal' },
  { icon: Calendar, label: 'My Events', path: '/portal/events' },
  { icon: CreditCard, label: 'Invoices', path: '/portal/invoices' },
  { icon: FileText, label: 'Documents', path: '/portal/documents' },
  { icon: MessageSquare, label: 'Messages', path: '/portal/messages' },
];

export const Portal = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [payingInvoiceId, setPayingInvoiceId] = useState(null);

  const currentPath = location.pathname;

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/events`, { withCredentials: true });
      setEvents(response.data);
      setSelectedEvent((prev) => prev || response.data[0] || null);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDocuments = useCallback(async (eventId) => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/documents`, { withCredentials: true });
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    }
  }, []);

  const fetchMessages = useCallback(async (eventId) => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/messages`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portal/invoices`, { withCredentials: true });
      setInvoices(response.data);
    } catch (error) {
      // Non-blocking — the invoices tab renders its own empty state.
      // Log for observability rather than swallowing silently.
      console.error('[portal] failed to load invoices', error);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchInvoices();
    }
  }, [user, fetchInvoices]);

  const handlePayInvoice = async (invoiceId) => {
    setPayingInvoiceId(invoiceId);
    try {
      const res = await axios.post(
        `${API}/payments/checkout`,
        { invoice_id: invoiceId, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start checkout');
      setPayingInvoiceId(null);
    }
  };

  useEffect(() => {
    if (user) {
      fetchEvents();
    }
  }, [user, fetchEvents]);

  useEffect(() => {
    if (selectedEvent) {
      fetchDocuments(selectedEvent.event_id);
      fetchMessages(selectedEvent.event_id);
    }
  }, [selectedEvent, fetchDocuments, fetchMessages]);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/');
  }, [logout, navigate]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedEvent) return;

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(',')[1];
      try {
        await axios.post(`${API}/events/${selectedEvent.event_id}/documents`, {
          event_id: selectedEvent.event_id,
          filename: file.name,
          file_type: file.type,
          file_data: base64
        }, { withCredentials: true });
        toast.success('Document uploaded successfully');
        fetchDocuments(selectedEvent.event_id);
      } catch (error) {
        toast.error('Failed to upload document');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.document_id}/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await axios.delete(`${API}/documents/${docId}`, { withCredentials: true });
      toast.success('Document deleted');
      fetchDocuments(selectedEvent.event_id);
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedEvent) return;

    try {
      await axios.post(`${API}/events/${selectedEvent.event_id}/messages`, {
        event_id: selectedEvent.event_id,
        content: newMessage
      }, { withCredentials: true });
      setNewMessage('');
      fetchMessages(selectedEvent.event_id);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#C6A86A] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) return null;

  const renderContent = () => {
    if (currentPath === '/portal' || currentPath === '/portal/events') {
      return (
        <div>
          <h2 className="font-heading text-2xl text-white mb-6">
            {currentPath === '/portal' ? 'Dashboard' : 'My Events'}
          </h2>
          
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[#0a0a0a] border border-white/10 p-6 animate-pulse">
                  <div className="h-4 bg-[#B8AEA1]/20 rounded w-1/2 mb-4" />
                  <div className="h-6 bg-[#B8AEA1]/20 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-[#B8AEA1]/20 rounded w-1/3" />
                </div>
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="bg-[#0a0a0a] border border-white/10 p-12 text-center">
              <Calendar className="w-12 h-12 text-[#B8AEA1] mx-auto mb-4" />
              <h3 className="font-heading text-xl text-white mb-2">No Events Yet</h3>
              <p className="text-white/60 text-sm mb-6">
                Your events will appear here once our team sets them up.
              </p>
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-[#1B1B1B] text-white text-sm uppercase tracking-widest hover:bg-[#111111] transition-colors">
                Contact Us
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((event) => (
                <motion.div
                  key={event.event_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-[#0a0a0a] border p-6 cursor-pointer transition-all ${
                    selectedEvent?.event_id === event.event_id 
                      ? 'border-[#C6A86A]' 
                      : 'border-white/10 hover:border-white/20'
                  }`}
                  onClick={() => setSelectedEvent(event)}
                  data-testid={`event-card-${event.event_id}`}
                >
                  <span className={`text-xs uppercase tracking-widest px-2 py-1 ${
                    event.status === 'planning' ? 'bg-blue-50 text-blue-600' :
                    event.status === 'confirmed' ? 'bg-green-50 text-green-600' :
                    event.status === 'completed' ? 'bg-[#B8AEA1]/20 text-white/60' :
                    'bg-[#D4AF37]/20 text-[#D4AF37]'
                  }`}>
                    {event.status}
                  </span>
                  <h3 className="font-heading text-xl text-white mt-4 mb-2">{event.title}</h3>
                  <p className="text-white/60 text-sm">{event.event_type}</p>
                  <div className="flex items-center gap-2 mt-4 text-white/40 text-sm">
                    <Calendar size={14} />
                    {event.event_date}
                  </div>
                  {event.venue && (
                    <p className="text-white/40 text-sm mt-1">{event.venue}</p>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (currentPath === '/portal/invoices') {
      const pending = invoices.filter((i) => i.status === 'pending');
      const paid = invoices.filter((i) => i.status === 'paid');
      const currency = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      return (
        <div data-testid="portal-invoices">
          <h2 className="font-heading text-2xl text-white mb-6">Invoices</h2>

          {invoices.length === 0 ? (
            <div className="bg-[#0a0a0a] border border-white/10 p-12 text-center">
              <CreditCard className="w-12 h-12 text-[#B8AEA1] mx-auto mb-4" />
              <h3 className="font-heading text-xl text-white mb-2">No Invoices Yet</h3>
              <p className="text-white/60 text-sm">Your invoices will appear here once your concierge creates one.</p>
            </div>
          ) : (
            <>
              {pending.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-white/40 text-[10px] uppercase tracking-widest mb-3">Outstanding</h3>
                  <div className="space-y-3">
                    {pending.map((inv) => (
                      <div key={inv.invoice_id} className="bg-[#0a0a0a] border border-[#D4AF37]/30 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid={`portal-invoice-${inv.invoice_id}`}>
                        <div>
                          <p className="text-white font-heading text-lg">{inv.title}</p>
                          {inv.description && (
                            <p className="text-white/50 text-sm mt-1">{inv.description}</p>
                          )}
                          <p className="text-[#D4AF37] font-mono text-2xl mt-3">{currency(inv.amount)}</p>
                        </div>
                        <button
                          onClick={() => handlePayInvoice(inv.invoice_id)}
                          disabled={payingInvoiceId === inv.invoice_id}
                          className="px-8 py-4 bg-[#D4AF37] text-black text-xs uppercase tracking-widest hover:bg-[#C9A432] disabled:opacity-50 whitespace-nowrap"
                          data-testid={`pay-invoice-${inv.invoice_id}`}
                        >
                          {payingInvoiceId === inv.invoice_id ? 'Redirecting…' : 'Pay Now'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {paid.length > 0 && (
                <div>
                  <h3 className="text-white/40 text-[10px] uppercase tracking-widest mb-3">Paid</h3>
                  <div className="space-y-3">
                    {paid.map((inv) => (
                      <div key={inv.invoice_id} className="bg-[#0a0a0a] border border-white/10 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                          <p className="text-white font-medium">{inv.title}</p>
                          <p className="text-white/40 text-xs mt-1">Paid on {inv.paid_at ? format(new Date(inv.paid_at), 'MMM d, yyyy') : '—'}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-white/70 font-mono">{currency(inv.amount)}</span>
                          <span className="text-[10px] uppercase tracking-widest bg-green-500/10 text-green-400 border border-green-500/30 px-2 py-1">Paid</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    if (currentPath === '/portal/documents') {
      return (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl text-white">Documents</h2>
            {selectedEvent && (
              <label className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#1B1B1B] text-white text-sm uppercase tracking-widest hover:bg-[#111111] transition-colors cursor-pointer" data-testid="upload-btn">
                <Upload size={16} />
                Upload
                <input
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </label>
            )}
          </div>

          {!selectedEvent ? (
            <div className="bg-[#0a0a0a] border border-white/10 p-12 text-center">
              <FileText className="w-12 h-12 text-[#B8AEA1] mx-auto mb-4" />
              <p className="text-white/60">Select an event to view documents</p>
            </div>
          ) : documents.length === 0 ? (
            <div className="bg-[#0a0a0a] border border-white/10 p-12 text-center">
              <FileText className="w-12 h-12 text-[#B8AEA1] mx-auto mb-4" />
              <h3 className="font-heading text-xl text-white mb-2">No Documents</h3>
              <p className="text-white/60 text-sm">
                Upload contracts, mood boards, or other files for your event.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map((doc) => (
                <div
                  key={doc.document_id}
                  className="bg-[#0a0a0a] border border-white/10 p-4 flex items-center justify-between"
                  data-testid={`document-${doc.document_id}`}
                >
                  <div className="flex items-center gap-4">
                    <FileText className="w-8 h-8 text-[#D4AF37]" />
                    <div>
                      <p className="text-white font-medium">{doc.filename}</p>
                      <p className="text-white/40 text-xs">
                        Uploaded by {doc.uploaded_by_name} • {format(new Date(doc.created_at), 'MMM d, yyyy')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleDownload(doc)}
                      className="p-2 text-white/60 hover:text-white transition-colors"
                      title="Download"
                    >
                      <Download size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteDocument(doc.document_id)}
                      className="p-2 text-white/60 hover:text-red-500 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (currentPath === '/portal/messages') {
      return (
        <div className="h-[calc(100vh-200px)] flex flex-col">
          <h2 className="font-heading text-2xl text-white mb-6">Messages</h2>

          {!selectedEvent ? (
            <div className="bg-[#0a0a0a] border border-white/10 p-12 text-center flex-1 flex flex-col items-center justify-center">
              <MessageSquare className="w-12 h-12 text-[#B8AEA1] mb-4" />
              <p className="text-white/60">Select an event to view messages</p>
            </div>
          ) : (
            <>
              <ScrollArea className="flex-1 bg-[#0a0a0a] border border-white/10 p-4 mb-4">
                {messages.length === 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <p className="text-white/40">No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.message_id}
                        className={`flex ${msg.sender_id === user.user_id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] p-4 ${
                            msg.sender_id === user.user_id
                              ? 'bg-[#D4AF37] text-white'
                              : 'bg-[#050505] text-white'
                          }`}
                        >
                          <p className="text-sm">{msg.content}</p>
                          <p className={`text-xs mt-2 ${
                            msg.sender_id === user.user_id ? 'text-white/70' : 'text-white/40'
                          }`}>
                            {msg.sender_name} • {format(new Date(msg.created_at), 'MMM d, h:mm a')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              <form onSubmit={handleSendMessage} className="flex gap-4">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 bg-[#0a0a0a] border border-white/10 px-4 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#C6A86A]"
                  data-testid="message-input"
                />
                <button type="submit" className="px-6 py-3 bg-[#1B1B1B] text-white hover:bg-[#111111] transition-colors" data-testid="send-message-btn">
                  <Send size={18} />
                </button>
              </form>
            </>
          )}
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-20" data-testid="portal-page">
      {/* Mobile Sidebar Toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-24 left-4 z-50 p-2 bg-[#0a0a0a] border border-white/10 text-white"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed lg:sticky top-20 left-0 h-[calc(100vh-5rem)] w-64 bg-[#0a0a0a] border-r border-white/10 p-6 z-40 transition-transform lg:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="mb-8">
            <p className="text-[#D4AF37] text-xs uppercase tracking-widest mb-2">Welcome</p>
            <p className="text-white font-medium truncate">{user?.name}</p>
            <p className="text-white/60 text-sm truncate">{user?.email}</p>
          </div>

          {selectedEvent && (
            <div className="mb-8 p-4 bg-[#050505] border border-white/10">
              <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Current Event</p>
              <p className="text-white font-heading truncate">{selectedEvent.title}</p>
            </div>
          )}

          <nav className="space-y-2">
            {sidebarLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                  currentPath === link.path
                    ? 'bg-[#D4AF37] text-white'
                    : 'text-white/60 hover:text-white hover:bg-[#050505]'
                }`}
                data-testid={`sidebar-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                <link.icon size={18} />
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="absolute bottom-6 left-6 right-6">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 text-white/60 hover:text-white transition-colors w-full px-4 py-3"
              data-testid="portal-logout"
            >
              <LogOut size={18} />
              Sign Out
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-12 min-h-[calc(100vh-5rem)]">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};
